import React, { useEffect, useMemo, useState } from 'react';
import { PlatformMember } from '../../../api/platform/coreNetwork';
import { formatDateTime, formatEmptyValue } from '../utils/format';
import {
  DEFAULT_VERIFICATION_REASONS,
  VerificationAction,
  canApproveVerification,
  canRejectVerification,
  canRevokeVerification,
  getNormalizedVerificationStatus,
  getVerificationStatusLabel,
  isVerificationApproved,
} from '../config/verification';
import { FieldGroup, InlineActions, KeyValueGrid, MvpCard, StatusBadge } from './MvpTalentUi';

interface VerificationActionPayload {
  action: VerificationAction;
  reason: string;
}

interface VerificationActionsProps {
  member: PlatformMember;
  loading?: boolean;
  onAction: (payload: VerificationActionPayload) => Promise<void> | void;
  compact?: boolean;
  capabilityCount?: number;
  membershipCount?: number;
}

interface TalentVerificationCardProps extends VerificationActionsProps {
  capabilityCount: number;
  membershipCount: number;
}

const ACTION_CONTENT: Record<VerificationAction, { title: string; label: string; description: string; tone?: 'danger' | 'default' }> = {
  approve: {
    title: 'Approve this talent?',
    label: 'Approve talent',
    description: 'Approval confirms that this talent is eligible to participate in the Core Network. Work Portal access will still need to be enabled separately.',
  },
  reject: {
    title: 'Reject this talent?',
    label: 'Reject talent',
    description: 'Rejection prevents Work Portal enablement and records the reason in the platform audit history.',
    tone: 'danger',
  },
  revoke: {
    title: 'Revoke verification?',
    label: 'Revoke verification',
    description: 'Revoking verification blocks new Work Portal invitations. Review existing portal access separately if this talent already has access.',
    tone: 'danger',
  },
};

export const VerificationStatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const normalized = getNormalizedVerificationStatus(status);
  return <StatusBadge status={getVerificationStatusLabel(normalized)} />;
};

export const VerificationActions: React.FC<VerificationActionsProps> = ({ member, loading = false, onAction, compact = false, capabilityCount, membershipCount }) => {
  const [dialogAction, setDialogAction] = useState<VerificationAction | null>(null);
  const [reason, setReason] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const normalizedStatus = getNormalizedVerificationStatus(member.verificationStatus);
  const activeContent = dialogAction ? ACTION_CONTENT[dialogAction] : null;
  const defaultReason = dialogAction ? DEFAULT_VERIFICATION_REASONS[dialogAction] : '';

  useEffect(() => {
    if (dialogAction) {
      setReason(DEFAULT_VERIFICATION_REASONS[dialogAction]);
      setConfirmationText('');
      setSubmitting(false);
    }
  }, [dialogAction]);

  const closeDialog = () => {
    if (submitting) return;
    setDialogAction(null);
  };

  const availableActions = useMemo(() => ({
    approve: canApproveVerification(normalizedStatus),
    reject: canRejectVerification(normalizedStatus),
    revoke: canRevokeVerification(normalizedStatus),
  }), [normalizedStatus]);

  return (
    <>
      <InlineActions>
        {availableActions.approve ? (
          <button type="button" className="mvp-button" onClick={() => setDialogAction('approve')} disabled={loading || submitting}>
            {loading ? 'Approving talent...' : 'Approve talent'}
          </button>
        ) : null}
        {availableActions.reject ? (
          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialogAction('reject')} disabled={loading || submitting}>
            Reject talent
          </button>
        ) : null}
        {availableActions.revoke ? (
          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialogAction('revoke')} disabled={loading || submitting}>
            Revoke verification
          </button>
        ) : null}
        {!availableActions.approve && !availableActions.reject && !availableActions.revoke && !compact ? <VerificationStatusBadge status={member.verificationStatus} /> : null}
      </InlineActions>

      {dialogAction && activeContent ? (
        <div className="mvp-dialog-backdrop" role="presentation" onClick={closeDialog}>
          <div className="mvp-dialog" role="dialog" aria-modal="true" aria-labelledby="verification-dialog-title" onClick={(event) => event.stopPropagation()}>
            <h3 id="verification-dialog-title">{activeContent.title}</h3>
            <p>{activeContent.description}</p>
            <KeyValueGrid items={[
              { label: 'Talent', value: member.displayName },
              { label: 'Email', value: formatEmptyValue(member.email) },
              { label: 'Company', value: formatEmptyValue(member.companyName) },
              { label: 'Current verification', value: <VerificationStatusBadge status={member.verificationStatus} /> },
              { label: 'Global status', value: <StatusBadge status={member.globalStatus} /> },
              { label: 'Capabilities', value: capabilityCount ?? 'Not loaded' },
              { label: 'Memberships', value: membershipCount ?? 'Not loaded' },
            ]} />
            <FieldGroup label="Administrative reason *">
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={4}
                aria-describedby="verification-reason-help"
                disabled={submitting}
                required
              />
            </FieldGroup>
            <div id="verification-reason-help" className="mvp-field-help">
              This reason is sent to Elite Core for audit history. {defaultReason ? 'You may edit the default review note before submitting.' : 'Enter a specific review note before submitting.'}
            </div>
            <label className="mvp-field">
              <span>Type CONFIRM to continue</span>
              <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} disabled={submitting} />
            </label>
            <div className="mvp-dialog__actions">
              <button type="button" className="mvp-button mvp-button--ghost" onClick={closeDialog} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className={`mvp-button ${activeContent.tone === 'danger' ? 'mvp-button--danger' : ''}`.trim()}
                disabled={submitting || loading || confirmationText !== 'CONFIRM' || !reason.trim()}
                onClick={async () => {
                  if (!dialogAction) return;
                  setSubmitting(true);
                  try {
                    await onAction({ action: dialogAction, reason: reason.trim() });
                    setDialogAction(null);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting || loading ? `${activeContent.label.replace(/ talent| verification/i, '')}...` : activeContent.label}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

const TalentVerificationCard: React.FC<TalentVerificationCardProps> = ({ member, capabilityCount, membershipCount, loading = false, onAction }) => (
  <MvpCard title="Verification">
    <div className="mvp-verification-card">
      <div className="mvp-verification-card__summary">
        <div>
          <p className="mvp-verification-card__eyebrow">Administrative review</p>
          <h4>{getVerificationStatusLabel(member.verificationStatus)}</h4>
          <p>
            Verification confirms that a talent has been reviewed for Core Network participation. Work Portal access remains a separate lifecycle step.
          </p>
        </div>
        <VerificationStatusBadge status={member.verificationStatus} />
      </div>
      <KeyValueGrid items={[
        { label: 'Verification status', value: <VerificationStatusBadge status={member.verificationStatus} /> },
        { label: 'Portal verified date', value: formatDateTime(member.portalVerifiedAt) },
        { label: 'Capabilities', value: capabilityCount },
        { label: 'Memberships', value: membershipCount },
      ]} />
      {isVerificationApproved(member.verificationStatus) ? (
        <div className="mvp-field-help">This talent is approved and eligible for Work Portal access. Enable portal access separately when you are ready to invite them.</div>
      ) : (
        <div className="mvp-field-help">Approve this talent before enabling Work Portal access.</div>
      )}
      <VerificationActions member={member} loading={loading} onAction={onAction} capabilityCount={capabilityCount} membershipCount={membershipCount} />
    </div>
  </MvpCard>
);

export default TalentVerificationCard;
